import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.tsx';

// ─── FailOpenBanner — admin-only warning that the access gate is failing OPEN ──────────────────
// The v143 lockout resolver (server/src/lib/planTier.ts) deliberately fails OPEN rather than
// locking everyone out when it can't resolve state -- a missing migration or an unresolvable
// org/user grants access instead of denying it, because wrongly locking a live facility mid-shift
// is far worse than wrongly letting an unpaid one through. The tradeoff is that a fail-open looks
// exactly like normal, healthy access from the outside: nothing in the UI changes, so the platform
// could sit ungated indefinitely without anyone noticing.
//
// This is the "someone noticed" part. Rendered only for role === 'admin' (the server only sends
// accessDiagnostics to that role at all), and only when the resolver actually took a degraded
// path -- a real subscription or bypass never trips it.
// Only the reasons that indicate a REAL problem. 'fail_open_no_org' is deliberately absent: a
// platform admin legitimately has no single org to gate against (their JWT carries orgId=null by
// design), so banner-ing it would fire on every healthy admin session and train the reader to
// ignore the banner -- exactly the failure mode this is meant to prevent. The API still reports
// failOpen:true for it honestly; it just isn't an alert.
const REASON_TEXT: Record<string, string> = {
  fail_open_schema_missing:
    'the lockout tables/columns are missing from this database (v141/v142/v143 not applied — run `npm run db:migrate`)',
  fail_open_row_not_found:
    'the current org/user did not resolve to a database row',
};

export default function FailOpenBanner() {
  const { user } = useAuth();
  const diag = user?.accessDiagnostics;

  // Only admins receive accessDiagnostics at all, so this is inherently admin-only -- the role
  // check is belt-and-braces in case the field is ever broadened server-side.
  if (!diag || !diag.failOpen || user?.role !== 'admin') return null;
  if (!(diag.reason in REASON_TEXT)) return null;

  const detail = REASON_TEXT[diag.reason] ?? `the resolver reported "${diag.reason}"`;

  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 14px',
        background: 'rgba(245,158,11,0.12)',
        borderBottom: '1px solid rgba(245,158,11,0.3)',
        color: '#f59e0b',
        fontSize: '0.78rem', fontWeight: 500,
        lineHeight: 1.35,
      }}
    >
      <ShieldAlert size={14} style={{ flexShrink: 0 }} />
      <span>
        <strong>Access gate is failing open (admin-only notice).</strong>{' '}
        No org or facility is being locked out right now because {detail}. Subscriptions and
        bypasses are not being enforced until this is resolved.
      </span>
    </div>
  );
}
